import styles from './CustomFieldChips.module.css';
import React, { useEffect, useState } from 'react';
import { CustomFieldOption } from '@he-novation/config/types/custom-field.types';
import { FormCustomField } from '@he-novation/config/types/form.types';
import { Button, ButtonTone } from '@he-novation/design-system/components/buttons/Button/Button';
import {
    FormField,
    FormFieldProps
} from '@he-novation/design-system/components/form/FormField/FormField';

type CustomFormFieldProps = {
    formId?: string;
    customField: FormCustomField;
    readOnly?: boolean;
    namePrefix?: string;
    value?: unknown;
    onChange?: (value: unknown) => void;
};

export function CustomFormField({
    formId,
    customField,
    readOnly,
    namePrefix = '',
    value,
    onChange
}: CustomFormFieldProps) {
    const [selectedOptions, setSelectedOptions] = useState<CustomFieldOption[]>([]);

    const identification = formId
        ? { formId, name: namePrefix + customField.name }
        : { id: 'custom-field-' + customField.uuid };

    const commonProps = {
        label: customField.name,
        readOnly: readOnly,
        disabled: readOnly,
        value: customField.type === 'DATE' && typeof value === 'string' ? new Date(value) : value,
        checked:
            customField.type === 'BOOLEAN' && value !== undefined && value !== null
                ? Boolean(value)
                : undefined,
        validation: {
            required: customField.required
        }
    };

    useEffect(() => {
        if (!Array.isArray(value) || customField.type !== 'SELECT' || !customField.options) return;

        setSelectedOptions(customField.options.filter((o) => value.includes(o.label)));
    }, [customField.type, value, customField.options]);

    switch (customField.type) {
        case 'DATE':
            return (
                <FormField
                    {...identification}
                    {...commonProps}
                    type={'date-picker'}
                    onChange={(_, value) => onChange?.(new Date(value))}
                />
            );
        case 'NUMBER':
            return (
                <FormField
                    {...identification}
                    {...commonProps}
                    type={'number'}
                    coerceType={'float'}
                    onChange={(e) => onChange?.(e.target.value)}
                />
            );
        case 'STRING':
            return (
                <FormField
                    {...identification}
                    {...commonProps}
                    type={'text'}
                    onChange={(e) => onChange?.(e.target.value)}
                />
            );
        case 'BOOLEAN':
            return (
                <FormField
                    {...identification}
                    {...commonProps}
                    type={'checkbox'}
                    className="is-switch"
                    onChange={(e) => onChange?.(e.target.checked)}
                />
            );
        case 'SELECT':
            if (!customField.options) return null;

            return (
                <FormField
                    {...identification}
                    {...commonProps}
                    type={'react-select'}
                    isMulti={customField.isMultiple}
                    hideSelectedOptions={true}
                    components={{
                        ClearIndicator: (): null => null,
                        MultiValue: (_): null => null
                    }}
                    required={customField.required}
                    options={customField.options!.map((option) => ({
                        value: option.label,
                        label: (
                            <div
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex'
                                }}
                            >
                                <span
                                    style={{
                                        backgroundColor: option.color || '#ffffff',
                                        borderRadius: '50%',
                                        height: '12px',
                                        marginRight: '8px',
                                        width: '12px'
                                    }}
                                />
                                <span>{option.label}</span>
                            </div>
                        )
                    }))}
                    value={
                        customField.isMultiple
                            ? selectedOptions.map(({ label }) => label)
                            : selectedOptions[0]?.label
                    }
                    onChange={(_, values: string[]) => {
                        const selected = customField.options!.filter((o) =>
                            values.includes(o.label)
                        );
                        setSelectedOptions(selected);
                        onChange?.(values);
                    }}
                    after={
                        <div className={styles.selectedChips}>
                            {selectedOptions.map(({ uuid, label, color }) => (
                                <span
                                    key={uuid}
                                    style={{ color: color || undefined }}
                                    className={styles.chips}
                                >
                                    {label}
                                    <Button
                                        icon={{ name: 'plus', stroke: color }}
                                        tone={ButtonTone.HoverableLight}
                                        onClick={() =>
                                            setSelectedOptions((prev) => {
                                                const selected = prev.filter(
                                                    (o) => o.label !== label
                                                );
                                                onChange?.(selected.map((o) => o.label));
                                                return selected;
                                            })
                                        }
                                        className={styles.removeButton}
                                    />
                                </span>
                            ))}
                        </div>
                    }
                />
            );
    }
}
